<template>
    <div :class="[$style.content, $style.mobile]">
        <Typography
            type="header"
            size="s"
            accent
            :responsive="false"
        >
            {{ $tr('gameContainer.partner_popup_tooltip_title') }}
        </Typography>

        <Typography
            is="p"
            size="s"
            :class="$style.description"
            :responsive="false"
        >
            {{ $tr('gameContainer.partner_block_description') }}
        </Typography>

        <div
            :class="$style.copy_section"
            data-interface-target="copy-export-game-link-block"
            @click="copyToClipboard(exportUrl)"
        >
            <Typography
                size="s"
                type="label"
                :class="$style.link"
                :responsive="false"
            >
                {{ copied ? $tr('gameContainer.share_popup_tooltip_copied') : displayedExportUrl }}
            </Typography>
            <IconButton
                color="carbon-400"
                size="s"
                interfaceTarget="copy-export-game-link-block"
                :class="$style.button_round_override"
                icon="copy"
            />
        </div>

        <Link
            :externalTo="partnerUrl"
            :class="$style.grid_row"
            target="_blank"
            data-interface-target="goto-start-monetizing-link-block"
        >
            <img
                :src="ImageDollarGold"
                :class="$style.icon"
            />
            <Typography
                size="m"
                type="label"
                :class="$style.start_monetizing"
                :responsive="false"
            >
                {{ $tr('gameContainer.partner_block_start_monetizing') }}
            </Typography>
            <IconButton
                color="carbon-400"
                size="m"
                interfaceTarget="goto-start-monetizing-link-block"
                :class="$style.button_round_gold_override"
                icon="chevron-right"
            />
        </Link>
    </div>
</template>

<script setup lang="ts">
import { computed, toRef } from 'vue'

import IconButton from '@/components/Buttons/IconButton.vue'
import Link from '@/components/Link.vue'
import Typography from '@/components/Typography.vue'
import { useClipboard } from '@/composables/use-clipboard'

import { usePartnerUrls } from '../../composables/use-partner-urls'
import ImageDollarGold from './icons/img-dollar-gold.webp'

const props = defineProps<{
    gameIcon: string
    hru: string
}>()

const { copied, copyToClipboard } = useClipboard()
const { exportUrl, partnerUrl } = usePartnerUrls(toRef(() => props.hru))

const displayedExportUrl = computed(() => {
    try {
        const url = new URL(exportUrl.value || '')
        return url.href.replace(`${url.protocol}//`, '')
    } catch {
        return exportUrl.value || ''
    }
})
</script>

<style module>
.grid_row {
    display: grid;
    grid-auto-flow: column;
    grid-template-columns: 40px;
    align-items: center;
    gap: 16px;
    transition: all 0.15s ease-in-out;
    padding-right: 4px;
}

.grid_row:hover {
    filter: brightness(1.2);
}

.grid_row > *:nth-child(3) {
    margin-left: auto;
}

.icon {
    color: #ffffff;
    width: 40px;
    height: 40px;
}

.description {
    color: rgba(255, 255, 255, 0.6);
}

.content {
    display: flex;
    flex-direction: column;
    gap: 16px;
    border-radius: 16px;
    background: var(--carbon-300);
    padding: 20px 16px;
}

.copy_section {
    display: flex;
    gap: 16px;
    height: 48px;
    padding: 0 4px 0 12px;
    align-items: center;
    align-self: stretch;
    border-radius: 1000px;
    background: var(--carbon-200);
    cursor: pointer;
}

.link {
    flex: 1;
    color: white;
    text-wrap: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.start_monetizing {
    flex: 1;
    color: var(--golden-400);
}

.mobile {
    display: flex;
}
@media (--desktop-small-plus) {
    .mobile {
        display: none;
    }
}

.button_round_override {
    width: 40px;
    height: 40px;
}

.button_round_gold_override {
    width: 40px;
    height: 40px;
    color: #ffc850;
}
</style>
